import { motion, useReducedMotion } from "framer-motion";
import {
  Gem,
  Gift,
  TrendingUp,
  MoonStar,
  Play,
  Sparkles,
} from "lucide-react";
import CelebrationButton from "@/components/ui/CelebrationButton";
import { SERVICES, waUrl } from "@/data/services";
import { useServiceTheme } from "@/context/ServiceThemeContext";

const ICONS = {
  wedding: Gem,
  gift: Gift,
  umkm: TrendingUp,
  masjid: MoonStar,
  influencer: Play,
};

function ServiceCard({ service, index }) {
  const { setActive } = useServiceTheme();
  const reduced = useReducedMotion();
  const Icon = ICONS[service.id];

  return (
    <motion.div
      initial={{ opacity: 0, y: 40 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-60px" }}
      transition={{ duration: 0.6, delay: index * 0.08, ease: [0.22, 1, 0.36, 1] }}
    >
      <motion.div
        animate={reduced ? {} : { y: [0, -8, 0] }}
        transition={{
          repeat: Infinity,
          duration: 5 + index * 0.7,
          ease: "easeInOut",
        }}
        onMouseEnter={() => setActive(service)}
        onFocus={() => setActive(service)}
        tabIndex={0}
        data-cursor-icon={service.id}
        data-testid={`service-card-${service.id}`}
        className="group relative flex h-full flex-col rounded-3xl border border-[#141318]/10 bg-white/80 p-6 shadow-[0_1px_2px_rgba(20,19,24,0.05),0_16px_40px_-16px_rgba(20,19,24,0.18)] backdrop-blur transition-shadow duration-300 hover:shadow-[0_2px_4px_rgba(20,19,24,0.05),0_28px_60px_-20px_rgba(20,19,24,0.28)]"
      >
        <div className="mb-5 flex items-center justify-between gap-3">
          <span
            className="flex h-11 w-11 items-center justify-center rounded-2xl"
            style={{ background: `${service.accent}1f`, color: service.accentDeep }}
          >
            <Icon size={20} />
          </span>
          <span className="truncate rounded-full bg-[#141318]/5 px-3 py-1 font-mono text-[10px] text-[#4A4A4A]">
            {service.url}
          </span>
        </div>
        <div className="relative mb-5 h-40 overflow-hidden rounded-2xl">
          <img
            src={service.image}
            alt={service.name}
            loading="lazy"
            decoding="async"
            fetchPriority="low"
            className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105"
          />
          <div
            className="absolute inset-0"
            style={{
              background: `linear-gradient(to top, ${service.accentDeep}55, transparent 55%)`,
            }}
          />
          {service.badge && (
            <span className="absolute right-3 top-3 rounded-full bg-[#C9A227] px-3 py-1 text-[10px] font-bold tracking-wider text-[#141318]">
              {service.badge}
            </span>
          )}
        </div>
        <h3 className="font-display text-2xl text-[#1A1A1A]">{service.name}</h3>
        <p className="mt-1 font-mono text-[11px] italic" style={{ color: service.accentDeep }}>
          {service.tagline}
        </p>
        <p className="mt-3 flex-1 text-sm leading-relaxed text-[#4A4A4A]">
          {service.desc}
        </p>
        <CelebrationButton
          variant={service.id}
          href={waUrl(service.waText)}
          testId={`service-cta-${service.id}`}
          className="mt-6 self-start"
        >
          {service.cta}
        </CelebrationButton>
      </motion.div>
    </motion.div>
  );
}

export default function Services() {
  const { setActive } = useServiceTheme();

  return (
    <section
      id="layanan"
      data-testid="services-section"
      className="relative bg-[#FDFBF7] py-24 lg:py-36"
    >
      <div className="mx-auto max-w-7xl px-6 lg:px-10">
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="mb-16 max-w-2xl"
        >
          <p className="font-mono text-[11px] tracking-[0.4em] text-[#B76E79]">
            01 — LIMA PORTAL PERAYAAN
          </p>
          <h2 className="font-display mt-4 text-4xl text-[#1A1A1A] md:text-5xl">
            Pilih caramu <em className="text-[#B76E79]">merayakan</em>
          </h2>
          <p className="mt-4 text-[#4A4A4A]">
            Arahkan kursor ke tiap kartu — lentera dan cahaya di sekelilingmu
            ikut berubah warna, mengikuti karakter perayaan yang kamu pilih.
          </p>
        </motion.div>
        <div
          className="grid gap-6 md:grid-cols-2 lg:grid-cols-3"
          onMouseLeave={() => setActive(null)}
        >
          {SERVICES.map((s, i) => (
            <ServiceCard key={s.id} service={s} index={i} />
          ))}
          <motion.div
            initial={{ opacity: 0, y: 40 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-60px" }}
            transition={{ duration: 0.6, delay: 0.4 }}
            className="flex h-full flex-col items-start justify-center rounded-3xl border-2 border-dashed border-[#141318]/15 p-8"
            data-testid="service-card-custom"
          >
            <Sparkles size={24} className="text-[#C9A227]" />
            <h3 className="font-display mt-4 text-2xl text-[#1A1A1A]">
              Punya ide perayaan lain?
            </h3>
            <p className="mt-3 text-sm leading-relaxed text-[#4A4A4A]">
              Wisuda, aqiqah, pembukaan toko — ceritakan momenmu. Kami suka
              tantangan baru.
            </p>
            <CelebrationButton
              variant="gold"
              href={waUrl(
                "Halo Ihtifaal! Saya punya ide perayaan di luar 5 layanan yang ada."
              )}
              testId="service-cta-custom"
              className="mt-6"
            >
              Ceritakan Ide-mu
            </CelebrationButton>
          </motion.div>
        </div>
      </div>
    </section>
  );
}
